import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Loader2, Users, Briefcase, UserCog } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as ReTooltip } from "recharts";

const TYPE_META = {
  ouvrier: { label: "Ouvrier", color: "#f97316", icon: Briefcase },
  commercial: { label: "Commercial", color: "#22c55e", icon: UserCog },
  administratif: { label: "Administratif", color: "#3b82f6", icon: Users }
};

const HRPilotageTab = () => {
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const empRes = await api.getEmployees();
        const actives = empRes.data.filter(e => !e.exit_date);
        setEmployees(actives);

        // Fetch stats for each employee in parallel
        const statsResults = await Promise.all(
          actives.map(e => api.getEmployeeStats(e.id, { year }).then(r => [e.id, r.data]).catch(() => [e.id, null]))
        );
        const sMap = {};
        statsResults.forEach(([id, s]) => { if (s) sMap[id] = s; });
        setStats(sMap);
      } catch {
        toast.error("Erreur chargement");
      } finally { setLoading(false); }
    })();
  }, [year]);

  // Aggregations
  const byType = useMemo(() => {
    const acc = { ouvrier: 0, commercial: 0, administratif: 0 };
    employees.forEach(e => { acc[e.employee_type || "ouvrier"] = (acc[e.employee_type || "ouvrier"] || 0) + 1; });
    return Object.entries(acc).map(([k, v]) => ({ type: k, count: v, ...TYPE_META[k] }));
  }, [employees]);

  const globals = useMemo(() => {
    let cpTotal = 0, cpUsed = 0, rttTotal = 0, rttUsed = 0;
    Object.values(stats).forEach(s => {
      cpTotal += s.balances?.cp?.initial || 0;
      cpUsed += s.balances?.cp?.used || 0;
      const rttS = s.balances?.rtt_salarie;
      const rttE = s.balances?.rtt_entreprise;
      rttTotal += (rttS?.annual_total || 0) + (rttE?.annual_total || 0);
      rttUsed += (rttS?.used || 0) + (rttE?.used || 0);
    });
    return {
      cpTotal, cpUsed, cpRemaining: Math.max(0, cpTotal - cpUsed),
      rttTotal, rttUsed, rttRemaining: Math.max(0, rttTotal - rttUsed)
    };
  }, [stats]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const active = employees.length;

  return (
    <div className="space-y-6" data-testid="hr-pilotage-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Effectif actif</div><div className="text-2xl font-bold">{active}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">CP restants (total)</div><div className="text-2xl font-bold text-sky-600">{globals.cpRemaining.toFixed(1)}<span className="text-sm text-muted-foreground ml-1">/ {globals.cpTotal}</span></div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">RTT restants (total)</div><div className="text-2xl font-bold text-cyan-600">{globals.rttRemaining.toFixed(1)}<span className="text-sm text-muted-foreground ml-1">/ {globals.rttTotal}</span></div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Jours consommés {year}</div><div className="text-2xl font-bold text-orange-600">{(globals.cpUsed + globals.rttUsed).toFixed(1)}</div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pie chart */}
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">Répartition par type</CardTitle></CardHeader>
          <CardContent>
            {active === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">Aucun salarié actif</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={byType.filter(t => t.count > 0)}
                    dataKey="count"
                    nameKey="label"
                    innerRadius={55}
                    outerRadius={90}
                    label={({ label, count }) => `${label}: ${count}`}
                  >
                    {byType.filter(t => t.count > 0).map((t) => (
                      <Cell key={t.type} fill={t.color} />
                    ))}
                  </Pie>
                  <ReTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}

            {/* Count badges */}
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {byType.map(t => {
                const Icon = t.icon;
                return (
                  <div key={t.type} className="flex items-center gap-1 text-sm">
                    <Icon className="h-4 w-4" style={{ color: t.color }} />
                    <span className="font-medium">{t.label}</span>
                    <Badge variant="outline">{t.count}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Consumption table */}
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Consommation CP / RTT {year}</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {employees.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">Aucun salarié actif</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Salarié</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>CP pris / total</TableHead>
                    <TableHead>RTT pris / acquis</TableHead>
                    <TableHead>%RTT consommés</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map(emp => {
                    const s = stats[emp.id];
                    const type = TYPE_META[emp.employee_type || "ouvrier"];
                    const cpUsed = s?.balances?.cp?.used || 0;
                    const cpTotal = s?.balances?.cp?.initial || 0;
                    const rttUsed = (s?.balances?.rtt_salarie?.used || 0) + (s?.balances?.rtt_entreprise?.used || 0);
                    const rttAccrued = (s?.balances?.rtt_salarie?.accrued || 0) + (s?.balances?.rtt_entreprise?.accrued || 0);
                    const rttAnnual = (s?.balances?.rtt_salarie?.annual_total || 0) + (s?.balances?.rtt_entreprise?.annual_total || 0);
                    const pct = rttAnnual > 0 ? Math.min(100, Math.round((rttUsed / rttAnnual) * 100)) : 0;
                    return (
                      <TableRow key={emp.id}>
                        <TableCell className="font-medium">{emp.first_name} {emp.last_name}</TableCell>
                        <TableCell><Badge style={{ backgroundColor: type.color + "20", color: type.color, borderColor: type.color + "40" }} variant="outline">{type.label}</Badge></TableCell>
                        <TableCell className="font-mono text-sm">{cpUsed} <span className="text-muted-foreground">/ {cpTotal}</span></TableCell>
                        <TableCell className="font-mono text-sm">{rttUsed} <span className="text-muted-foreground">/ {rttAccrued.toFixed(2)}</span> <span className="text-xs text-muted-foreground">(ann. {rttAnnual})</span></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={pct} className="h-2 w-20" />
                            <span className="text-xs font-mono w-10 text-right">{pct}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HRPilotageTab;
